<template>
  <Card>
    <div class="general">
      <div class="column" v-bind:class="firstColumn">
        <div>
          <div class="cardTitle">{{ registryNumber ? registryNumber : $t('Новая процедура') }}
            <openCloseIconInfo
              v-if="registryNumber"
              :isClosed="!!isClosed"
            />
          </div>
          <div v-if="!isEdit" class="cardSubTitle">{{ getCurrentTypeName }}</div>
        </div>
        <SelectInput
          v-if="isEdit"
          v-model="procedureType"
          :label="typeLabel"
          :items="types"
          :rules="typeRules"
          :disabled="disabled || !canChangeType"
          :is-edit="isEdit"
        />
        <TextArea
          v-if="isEdit"
          v-model="procedureName"
          :rules="nameRules"
          :label="nameLabel"
          :disabled="disabled"
        />
        <div v-else class="procedureName">
          {{ procedureName }}
        </div>
        <div
          v-if="typeof maxStartPrice !== 'undefined' && !generalInfoStore.hideStartMaxPrice"
          class="maxStartPrice"
        >{{ $t('Начальная (максимальная) цена:') }} <span class="maxStartPriceNum">
            {{ isWithoutStartMaxPrice ? $t('Без объявления цены') : maxStartPricePrepared }}
          </span>
        </div>
      </div>
      <div v-if="!isEdit" class="column second">
        <slot></slot>
      </div>
    </div>
    <component
      v-if="customGeneralComponent && procedure"
      :is="customGeneralComponent"
      :procedure="procedure"
    />
  </Card>
</template>

<script>
import api from '@/api';
import { toast } from '@/lib/toast';
import Card from "@intec/vue-components/form/Card.vue";
import StatusInfo from "./StatusInfo.vue";
import TextArea from "@intec/vue-components/form/TextArea.vue";
import SelectInput from "@intec/vue-components/form/SelectInput.vue";
import OpenCloseIconInfo from "@/components/procedure/OpenCloseIconInfo.vue";
import { formatMoneyText } from "../../filters";
import { useGeneralInfoStore } from "@/stores/generalInfoStore";
import { useGeneralInfoComponentStore } from "@/stores/generalInfoComponentStore";
import { useLibraryStore } from "@/stores/library";
import { onMounted, computed } from "vue";

export default {
  name: 'GeneralInfo',
  components: {
    OpenCloseIconInfo,
    SelectInput,
    TextArea,
    StatusInfo,
    Card,
  },
  props: {
    procedure: {
      type: Object,
      required: false
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
    canChangeType: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    isClosed: {
      type: Boolean,
      default: false,
    },
    procedureId: {
      type: Number,
    },
    registryNumber: {
      type: String
    },
    name: {
      type: String,
    },
    nameLabel: {
      type: String,
      default: 'Наименование процедуры',
    },
    type: {
      type: String,
      default: null,
    },
    typeLabel: {
      type: String,
      default: 'Способ закупки',
    },
    types: {
      type: Array,
      default: [],
    },
    maxStartPrice: {},
    isWithoutStartMaxPrice: {
      type: Boolean,
      default: false
    },
    isNds: {
      type: Boolean,
      default: null
    },
    nameRules: {},
    typeRules: {},
  },
  emits: ['update:name', 'update:type'],
  data() {
    return {};
  },
  setup(props) {
    const generalInfoStore = useGeneralInfoStore();
    const libraryStore = useLibraryStore();
    const componentStore = useGeneralInfoComponentStore();

    onMounted(() => {
      if (props.procedure) {
        libraryStore.callActionByCode('initGeneralInfoOptions', {
          procedureType: props.procedure.type,
          procedureStatus: props.procedure.step
        });
      }
    });

    const customGeneralComponent = computed(() => {
      return props.procedure?.type
        ? componentStore.getComponentByType(props.procedure.type)
        : null;
    });

    return {
      generalInfoStore,
      customGeneralComponent
    };
  },
  methods: {
    formatMoneyText,
    async changeProcedureType(type) {
      try {
        let formData = new FormData();
        formData.append('type', JSON.stringify(type));
        await api.post(`/procedures/changeType/${this.procedureId}`, formData);
      } catch (e) {
        toast.error(e.message);
      }
    },
  },
  computed: {
    procedureName: {
      get() {
        return this.name
      },
      set(value) {
        this.$emit('update:name', value)
      }
    },
    procedureType: {
      get() {
        return this.type
      },
      set(value) {
        this.$emit('update:type', value)
        if (this.isEdit) {
          this.changeProcedureType(value);
        }
      }
    },
    firstColumn() {
      return this.isEdit ? 'firstExtend' : 'first';
    },
    maxStartPricePrepared() {
      return formatMoneyText(this.maxStartPrice);
    },
    getCurrentTypeName() {
      let index = this.types.findIndex((elem) => elem.value === this.type);
      if (index !== -1) {
        return this.types[index].title;
      }
      return '';
    }
  }
};
</script>
<style scoped>
.maxStartPrice {
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 22px;
  color: rgba(var(--black-color), 0.6);
}

:deep(.v-label::after) {
  content: ' *';
  color: rgba(var(--error-color));
}

.maxStartPriceNum {
  font-weight: 500;
  font-size: 20px;
  line-height: 27px;
  color: rgb(var(--black-color));
}

.procedureName {
  font-weight: 400;
  font-size: 16px;
  line-height: 22px;
  color: rgba(var(--black-color), 0.8);
}

.general {
  display: flex;
  justify-content: space-between;
  flex-direction: row;
  gap: 26px;
  align-items: stretch;
}

.column {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: space-between;
  gap: 15px;
}

.column.first {
  flex-basis: 60%;
}

.column.firstExtend {
  flex-basis: 100%;
}

.column.second {
  flex-basis: 40%;
}

:deep(.v-field--disabled) {
  background-color: rgb(var(--white-color));
  opacity: 0.38;
}
</style>
